/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useErrorState } from '../../shared/useErrorState';
import { Textarea, Box } from 'chakra-v3';
import type { UserTextComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams, getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import {
  EmptyInputError,
  MinLengthError,
  MaxLengthError,
  InvalidIntegerError,
  InvalidNumberError,
  MinValueError,
  MaxValueError,
} from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';

export const UserText: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const params = getTypedParams<UserTextComponent>(node);
  const [value, setValue] = useState<string>(params.pre_fill ?? '');
  const [errorState, setErrorState] = useErrorState();
  const error: Error | string | null = errorState;
  const setError = useCallback(
    (errorFactory: (() => Error) | string | null) => {
      if (errorFactory === null) {
        setErrorState(null);
        return;
      }
      if (typeof errorFactory === 'string') {
        // For string errors (like regexError), create Error object
        setErrorState(() => new Error(errorFactory));
        return;
      }
      setErrorState(errorFactory);
    },
    [setErrorState],
  );

  const minLength = params.min_length;
  const maxLength = params.max_length;
  const min = params.min;
  const max = params.max;
  const type = params.type ?? 'TEXT';
  const fullScreen = params.full_screen ?? false;
  const regex = params.regularExpression;
  const regexError = params.regexMismatchError ?? 'Invalid input';

  useEffect(() => {
    if (params.pre_fill) {
      setValue(params.pre_fill);
    }
  }, [params.pre_fill]);

  const teardownFn = useCallback(
    (next: () => void) => {
      // Validate empty/whitespace-only input
      const trimmedValue = value.trim();
      if (trimmedValue.length === 0) {
        setError(() => new EmptyInputError(minLength));
        return;
      }
      if (minLength !== undefined && trimmedValue.length < minLength) {
        setError(() => new MinLengthError(minLength));
        return;
      }
      if (maxLength !== undefined && trimmedValue.length > maxLength) {
        setError(() => new MaxLengthError(maxLength));
        return;
      }
      if (type === 'INTEGER' || type === 'FLOAT') {
        let numValue: number;

        if (type === 'INTEGER') {
          // Validate integer format: optional sign + only digits
          const integerRegex = /^-?\d+$/;
          if (!integerRegex.test(trimmedValue)) {
            setError(() => new InvalidIntegerError());
            return;
          }
          numValue = Number(trimmedValue);
          // Additional safety check (should not be needed after regex, but just in case)
          if (isNaN(numValue) || !isFinite(numValue)) {
            setError(() => new InvalidIntegerError());
            return;
          }
          if (min !== undefined && numValue < min) {
            setError(() => new MinValueError(min));
            return;
          }
          if (max !== undefined && numValue > max) {
            setError(() => new MaxValueError(max));
            return;
          }
        } else {
          // FLOAT: Validate float format (optional sign, digits, optional decimal point, digits)
          const floatRegex = /^-?\d*\.?\d+$/;
          if (!floatRegex.test(trimmedValue)) {
            setError(() => new InvalidNumberError('number'));
            return;
          }
          numValue = parseFloat(trimmedValue);
          // Check for NaN and Infinity
          if (isNaN(numValue) || !isFinite(numValue)) {
            setError(() => new InvalidNumberError('number'));
            return;
          }
          if (min !== undefined && numValue < min) {
            setError(() => new MinValueError(min));
            return;
          }
          if (max !== undefined && numValue > max) {
            setError(() => new MaxValueError(max));
            return;
          }
        }
      } else if (regex) {
        try {
          if (!new RegExp(regex).test(trimmedValue)) {
            setError(regexError);
            return;
          }
        } catch (err) {
          // Invalid regex pattern - log but don't block submission
          console.error('Invalid regex pattern:', err);
        }
      }

      setError(null);

      // Write output - text is always an output param for UserTextComponent
      const component = getTypedComponent<UserTextComponent>(node);
      controller.writeOutput(component.text, trimmedValue);

      // Only call next() when validation passes and output is written
      next();
    },
    [value, minLength, maxLength, min, max, type, regex, regexError, node, controller, setError],
  );

  useNext({
    controller,
    teardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  // Always use Textarea for multiline support
  const InputComponent = Textarea;

  return (
    <Box
      border="1px solid"
      borderColor="border.white20"
      borderRadius="4px"
      bg="bg.inputGradient"
      p="16px"
      w="100%"
      display="flex"
      flexDirection="column"
      gap="12px"
      mx="auto"
    >
      {error && (
        <Text variant="caption" color="error.solid">
          {typeof error === 'string' ? error : error?.message}
        </Text>
      )}
      <InputComponent
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        minLength={minLength}
        maxLength={maxLength}
        placeholder="Add your feedback here"
        disabled={!isCurrent}
        rows={fullScreen ? 8 : 4}
        resize="vertical"
        bg="transparent"
        border="none"
        color="text.primary"
        fontFamily="body"
        _placeholder={{
          color: 'text.placeholder',
        }}
        _focus={{
          outline: 'none',
          boxShadow: 'none',
        }}
        p="0"
      />
    </Box>
  );
};
